import { SINGLE_FILE_TYPES, TYPE_DIAGNOSTIC, UPLOAD_LIMITS, hiTypeLabel } from '../../../constants/abdmHiTypes';
import { API_MODE, ENCOUNTER_KIND } from '../../../constants/hipRecordEnums';
import { UPLOAD } from '../../../constants/hipRecordsCopy';
import { formatDateIST } from '../../../utils/formatIST';

/**
 * Pure rules of the inline Add file / Replace panel (OR-82; moved out of the old upload drawer). No React, no requests.
 * Record-type rules (SINGLE_FILE_TYPES, UPLOAD_LIMITS) live in the shared constants (abdmHiTypes).
 */

const MB = 1024 * 1024;
const ACCEPTED_EXT = ['pdf', 'jpg', 'jpeg', 'png'];
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
// The one hidden file input of a card. No `capture`: there is no camera button (OR-82).
export const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

// Where the panel is in its life (draft.phase): EDIT = the user is filling it in; the others are the request running.
export const PHASE = Object.freeze({
  EDIT: 'EDIT', UPLOADING: 'UPLOADING', LINKING: 'LINKING', REPLACING: 'REPLACING',
});

// draft.banner.kind: an error (red, role=alert) or the "finish first" status line (amber, role=status).
export const BANNER = Object.freeze({ ERROR: 'ERROR', STATUS: 'STATUS' });

// How the card answers a failed upload (build-plan 4.3 codes). Any other code (network, 5xx, unknown) = UPLOAD.uploadError.
export const UPLOAD_ERROR_CODES = Object.freeze({
  // The file itself was refused (scanner, size, type, empty): shown with the server's sentence, the file is dropped.
  FILE: new Set(['FILE_REJECTED', 'FILE_TOO_LARGE', 'FILE_EMPTY', 'FILE_TYPE_NOT_ALLOWED']),
  // The record is full: the server's sentence, the files stay.
  KEEP: new Set(['TOO_MANY_FILES', 'RECORD_TOO_LARGE']),
  // The card changed under us: the server's sentence as a card line, the panel closes, the card refetches.
  CARD: new Set(['SLOT_LOCKED', 'ENCOUNTER_NOT_FOUND', 'ABHA_NOT_VERIFIED', 'TYPE_NOT_ALLOWED', 'VISIT_PATIENT_MISMATCH', 'RX_NOT_APPROVED']),
});
export const FILE_REJECTED = 'FILE_REJECTED';

// ── Helpers ───────────────────────────────────────────────────────────────────────────────
// OR-48: a Diagnostic Report's default name = its file name without the extension (max 100, the input's cap).
export const fileStem = (name) => (name && name.lastIndexOf('.') > 0 ? name.slice(0, name.lastIndexOf('.')) : name || '').slice(0, 100);
const extOf = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');
// Rounded UP so a file over the limit never reads "10.0 MB".
const limitMbText = (bytes) => (Math.ceil((bytes / MB) * 10) / 10).toFixed(1);
export const sizeText = (bytes) => (bytes < MB ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / MB).toFixed(1)} MB`);
export const kindText = (name) => (extOf(name) === 'jpeg' ? 'JPG' : extOf(name).toUpperCase());

/**
 * Client-side mirror of the server's upload rules. Returns null when the pick is fine, else the sentence to show.
 * A bad pick is discarded whole (nothing half-added). `existing` = the files the draft already holds (count + bytes): the
 * draft is what one upload request carries.
 */
export function checkPickedFiles(picked, { single, existing }) {
  if (!picked.length) return null;
  if (single && existing.count + picked.length > 1) return UPLOAD.singleFileHint; // QA-R6-1: counts the file already in the draft
  // One request carries the whole draft (the server refuses more than filesPerUpload files in one upload).
  if (existing.count + picked.length > UPLOAD_LIMITS.filesPerUpload) return UPLOAD.tooManyFiles(UPLOAD_LIMITS.filesPerUpload);
  for (const f of picked) {
    if (!ACCEPTED_EXT.includes(extOf(f.name)) || (f.type && !ACCEPTED_MIME.includes(f.type))) return UPLOAD.fileTypeError(f.name);
    if (f.size === 0) return UPLOAD.fileEmptyError(f.name);
    if (f.size > UPLOAD_LIMITS.fileBytes) return UPLOAD.sizeError(limitMbText(f.size));
  }
  const totalBytes = existing.bytes + picked.reduce((sum, f) => sum + f.size, 0);
  if (totalBytes > UPLOAD_LIMITS.recordBytes) return UPLOAD.recordTooBig(UPLOAD_LIMITS.recordBytes / MB);
  return null;
}

export const isSingleFile = (recordType) => SINGLE_FILE_TYPES.includes(recordType);

// OR-59: the panel asks for "Doctor for patient" (and sends it) only on a NEW link from a visit with no doctor. Replace never links.
export const needsDoctor = (draft, record) => draft.mode === API_MODE.ADD && !!record.needsPractitioner;

// A fresh draft for `target` = { mode, recordType, slotKey? } (HipEncounterCard): no file yet, the name untouched (null).
export const newDraft = ({ mode, recordType, slotKey }) => ({
  mode, recordType, slotKey, files: [], title: null, practitionerId: '', banner: null, phase: PHASE.EDIT, confirmOpen: false,
});

/**
 * The draft after `picked` is chosen: the files added (a bad pick is refused whole and the files already there stay,
 * with the error as the banner). The panel's own state, one per card (HipEncounterCard owns it).
 */
export function addPickedFiles(draft, picked) {
  const existing = { count: draft.files.length, bytes: draft.files.reduce((sum, f) => sum + f.size, 0) };
  const message = checkPickedFiles(picked, { single: isSingleFile(draft.recordType), existing });
  if (message) return { ...draft, banner: { kind: BANNER.ERROR, text: message } };
  return { ...draft, files: [...draft.files, ...picked], banner: null };
}

// The date in a default name: the visit date (OPD) / the discharge date (IPD).
const recordDateText = (record) => formatDateIST(record.encounterKind === ENCOUNTER_KIND.OPD
  ? (record.visitDate || record.encounterAt)
  : (record.dischargedAt || record.encounterAt));

/**
 * The name the panel shows and sends. `draft.title === null` = untouched: OR-48 / OR-49 a Diagnostic Report's name is the
 * first file's name without extension (follows the file), every other type is "<Type> - <date>" (plain dash, OR-47).
 * An edit sticks (also across a refused file).
 */
export function draftTitle(draft, record) {
  if (draft.title !== null) return draft.title;
  return draft.recordType === TYPE_DIAGNOSTIC
    ? fileStem(draft.files[0]?.name)
    : UPLOAD.titleDefault(hiTypeLabel(draft.recordType), recordDateText(record));
}
